'use client'

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { fetchInstrumentList } from '@/service/instrument.api'
import { isExchangeEnabled, useVenues } from '@/service/venue.hook'
import { Exchange, Instrument, TradeVenue } from '@/types/generated/common/types'

// Query keys
export const instrumentKeys = {
  all: ['instrument'] as const,
  list: (exchange: Exchange) => [...instrumentKeys.all, 'list', exchange] as const,
}

// Shared query options (queryKey/queryFn/select) so any caller that fetches
// multiple exchanges' instrument lists directly (e.g. via useQueries, when the
// set of exchanges needed is dynamic and can't be N useInstrumentList calls)
// applies the exact same filtering as useInstrumentList below — otherwise the
// two code paths can silently drift (e.g. one includes btcdvol instruments,
// the other doesn't) despite sharing the same query key/cache entry.
//
// Module-level so its identity is stable: TanStack only reuses the cached
// selection while `select` is the same function, so an inline closure re-ran
// the filter and `replaceEqualDeep` over the whole catalog on every render.
type InstrumentListResult = Awaited<ReturnType<typeof fetchInstrumentList>>
// Query observers each run select independently. Reuse the filtered catalog across
// widgets so mounting a history/order sheet does not copy every venue's instruments.
const tradeableCatalogs = new WeakMap<InstrumentListResult, InstrumentListResult>()
const selectTradeableInstruments = (data: InstrumentListResult) => {
  const cached = tradeableCatalogs.get(data)
  if (cached) return cached
  const selected = {
    ...data,
    instruments: data.instruments.filter(
      (instrument) => !instrument.symbol.toLowerCase().includes('btcdvol')
    ),
  }
  tradeableCatalogs.set(data, selected)
  return selected
}

export function instrumentListQueryOptions(exchange: Exchange) {
  return {
    queryKey: instrumentKeys.list(exchange),
    queryFn: () => fetchInstrumentList({ exchange }),
    staleTime: 10 * 60 * 1000,
    // Catalogs are large (hundreds of KB gzipped per venue, decoded into tens
    // of thousands of instruments) and change on the order of hours. Refetching
    // them on every focus/reconnect means a tab coming back from a long
    // absence re-downloads and re-decodes every mounted venue's catalog at
    // once — 15+ requests, ~1 MB, a multi-hundred-MB heap spike — right when
    // the sockets are also resubscribing and re-dumping. The hourly
    // refetchInterval on useInstrumentList (and the 10-min staleTime for any
    // fresh mount) is enough to keep them current.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // EXCHANGE_UNSPECIFIED is the proto zero-value, not a real exchange — it
    // shows up here transiently whenever a caller derives its exchange from
    // useAccountPrimaryExchange() and that query is still pending. Firing the
    // request anyway just produces a guaranteed "no instruments found" from
    // the backend for a value nobody actually wants instruments for.
    enabled: exchange !== Exchange.EXCHANGE_UNSPECIFIED,
    select: selectTradeableInstruments,
  }
}

// Instrument list query. `enabled` additionally gates the fetch (e.g. a
// caller that fetches a fixed set of exchanges via useExchangesEnabled, to
// honour a server-side venue disable) — ANDed with the EXCHANGE_UNSPECIFIED
// guard already computed in instrumentListQueryOptions, never overriding it.
// Also self-gates on GetVenues' own enabled flag (isExchangeEnabled) — the same
// backend-driven disable that keeps this from polling an exchange with no
// instrument catalog (e.g. Binance in prod) forever, without the frontend
// having to special-case any particular exchange.
export const useInstrumentList = (exchange: Exchange, enabled = true) => {
  const options = instrumentListQueryOptions(exchange)
  const { data: venuesData } = useVenues()
  return useQuery({
    ...options,
    enabled: enabled && options.enabled && isExchangeEnabled(venuesData?.venues, exchange),
    refetchInterval: 60 * 60 * 1000,
    // A "no instruments for this exchange" response (e.g. a linked account on
    // an exchange the backend hasn't seeded instruments for) is a deterministic
    // server response, not a flaky network error — retrying just repeats the
    // same failure 3x with backoff for no benefit.
    retry: false,
  })
}

// Resolves a given tradeVenue's primary-exchange instrument list for call
// sites that only have a bare currency/symbol in hand (no Instrument passed
// down from a parent) — e.g. a table cell renderer, which renders as its
// own top-level component with no access to the parent's closure state.
// Callers resolve their own tradeVenue (e.g. via useCardAccountContext) —
// this hook no longer reads exchangeAtom internally.
export function useResolvedInstruments(tradeVenue: TradeVenue | undefined): Instrument[] {
  const primaryExchange = useAccountPrimaryExchange(tradeVenue)
  const { data: instrumentListData } = useInstrumentList(primaryExchange)
  return useMemo(() => instrumentListData?.instruments ?? [], [instrumentListData])
}
